<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { useCallCenterStore, useLeadListStore, useCsvStore } from '@/stores';
import type { Campaign } from '@/apis/generated/dialbunny';
import { getCallCenterIdFromCampaign } from '@/campaign/utils/utils';
import { DATA_QA } from './campaign-basic-info.constants';
import logger from '@/logger';

const log = logger.getChild('CampaignBasicInfo');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});
const { $t } = useI18N();

type Props = {
  campaign: Campaign;
};

const props = defineProps<Props>();

const callCenterStore = useCallCenterStore();
const leadListStore = useLeadListStore();
const csvStore = useCsvStore();

const leadList = leadListStore.watch(
  () => props.campaign.leadListId ?? null,
  'CampaignBasicInfo',
);
const callCenter = callCenterStore.watch(
  () => getCallCenterIdFromCampaign(props.campaign) ?? null,
  'CampaignBasicInfo',
);
const callerId = computed(() => props.campaign.callerId ?? null);
const csv = csvStore.watch(
  () => leadList.value.item?.csvIds?.[0] ?? null,
  'CampaignBasicInfo',
);

const didDetail = computed(() =>
  callCenter.value.item?.didsDetails?.find(
    (did) => did.number === callerId.value,
  ),
);

const loading = computed(() => {
  return (
    callCenter.value.loading || leadList.value.loading || csv.value.loading
  );
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT" :data-qa-loading="loading" class="d-stack16">
    <div :data-qa="DATA_QA.CALL_CENTER">
      <p class="d-fs-100 d-lh-200 d-fc-tertiary d-mb4">
        {{ $t('CAMPAIGN_BASIC_INFO_CALL_CENTER_LABEL') }}
      </p>
      <p :data-qa="DATA_QA.CALL_CENTER_VALUE" class="d-lh-300">
        {{ callCenter.item?.name }}
      </p>
    </div>
    <div :data-qa="DATA_QA.CALLER_ID">
      <p class="d-fs-100 d-lh-200 d-fc-tertiary d-mb4">
        {{ $t('CAMPAIGN_BASIC_INFO_CALLER_ID_LABEL') }}
      </p>
      <p :data-qa="DATA_QA.CALLER_ID_VALUE" class="d-lh-300">
        {{ didDetail?.display }}
      </p>
    </div>
    <div :data-qa="DATA_QA.LIST">
      <p class="d-fs-100 d-lh-200 d-fc-tertiary d-mb4">
        {{ $t('CAMPAIGN_BASIC_INFO_LIST_LABEL') }}
      </p>
      <p :data-qa="DATA_QA.LIST_VALUE" class="d-lh-300">
        {{ csv.item?.name }}
        <span class="d-fc-tertiary">
          {{
            $t('CAMPAIGN_BASIC_INFO_LIST_RECORD_COUNTER', {
              count: csv.item?.importResults?.total
                ? csv.item.importResults.total
                : 0,
            })
          }}
        </span>
      </p>
    </div>
  </div>
</template>
